import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { cloneListOfCloneable, isSameData, isSameList } from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { BrandProfile } from './brand-profile.js';
import { EventInfo } from './event-info.js';

export class BrandPageInfo extends DataModel {
  static readonly TYPE_ID: string = '861310ba6b984cbb844efc74f90738c7';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly brand: BrandProfile,
    public readonly events: EventInfo[],
    public readonly metaPixelId: string | null,
    public readonly tikTokPixelId: string | null,
    public readonly googleTagManagerId: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return BrandPageInfo.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BrandPageInfo.SCHEMA_VERSION);
  }

  static create(args: {
    brand: BrandProfile,
    events: EventInfo[],
    metaPixelId: string | null,
    tikTokPixelId: string | null,
    googleTagManagerId: string | null,
  }): BrandPageInfo {
    return new BrandPageInfo(
        args.brand,
        args.events,
        args.metaPixelId,
        args.tikTokPixelId,
        args.googleTagManagerId,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BrandPageInfo {
    return BrandPageInfo.create({
      brand: context.fromData(JSONUtils.decodeRecord(MapUtils.get<unknown>(map, 'brand'))),
      events: MapUtils.getList<EventInfo>(
          map,
          'events',
          (data) => EventInfo.fromMap(context, JSONUtils.decodeRecord(data)),
      ),
      metaPixelId: MapUtils.getStringOrNull(map, 'metaPixelId'),
      tikTokPixelId: MapUtils.getStringOrNull(map, 'tikTokPixelId'),
      googleTagManagerId: MapUtils.getStringOrNull(map, 'googleTagManagerId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      brand: context.toData(this.brand),
      events: this.events.map((event) => event.toMap(context)),
      metaPixelId: this.metaPixelId,
      tikTokPixelId: this.tikTokPixelId,
      googleTagManagerId: this.googleTagManagerId,
    };
  }

  clone(): this {
    return BrandPageInfo.create({
      brand: this.brand.clone(),
      events: cloneListOfCloneable(this.events),
      metaPixelId: this.metaPixelId,
      tikTokPixelId: this.tikTokPixelId,
      googleTagManagerId: this.googleTagManagerId,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.brand, this.brand) &&
        isSameList(other.events, this.events, (a, b) => isSameData(a, b)) &&
        other.metaPixelId === this.metaPixelId &&
        other.tikTokPixelId === this.tikTokPixelId &&
        other.googleTagManagerId === this.googleTagManagerId;
  }
}

export class BrandPageInfoBuilder extends DataModelBuilder<BrandPageInfo> {
  build(context: SerializationContext, map: SerializedData): BrandPageInfo {
    return BrandPageInfo.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BrandPageInfo.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BrandPageInfo.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {version = 2;}

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
